/**
 * User preference slice — session cache for current-user UI preferences.
 *
 * Stores keyed preference records from all-user, prefix, or exact-key loads so
 * repeated useUserPreference consumers read local state instead of issuing one
 * GET per key.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { extractThunkError } from '@sophocles/api/thunkUtils';
import { fetchUserPreferences } from '@sophocles/features/userPreferences/api';
import {
  getPreferenceHydration,
  mergePreferenceRecords,
  normalizeUserPreferenceResponse,
} from '@sophocles/features/userPreferences/cache';

function normalizeLoadArg(arg) {
  return {
    key: arg?.key ?? null,
    keyPrefix: arg?.keyPrefix ?? null,
  };
}

function getInitialStatus() {
  return { loaded: false, loading: false, error: null };
}

export const loadUserPreferences = createAsyncThunk(
  'userPreferences/load',
  async (arg, { rejectWithValue }) => {
    const scope = normalizeLoadArg(arg);
    try {
      const data = await fetchUserPreferences(scope);
      return { scope, records: normalizeUserPreferenceResponse(data) };
    } catch (e) {
      return rejectWithValue({ scope, error: extractThunkError(e) });
    }
  },
  {
    condition: (arg, { getState }) => {
      const scope = normalizeLoadArg(arg);
      const state = getState().userPreferences;
      if (scope.key) {
        const hydration = getPreferenceHydration(state, scope.key);
        return !hydration.loaded && !hydration.loading;
      }
      if (scope.keyPrefix) {
        if (state.allLoaded || state.allLoading) return false;
        const status = state.prefixStatus[scope.keyPrefix];
        return !status?.loaded && !status?.loading;
      }
      return !state.allLoaded && !state.allLoading;
    },
  },
);

const slice = createSlice({
  name: 'userPreferences',
  initialState: {
    byKey: {},
    allLoaded: false,
    allLoading: false,
    allError: null,
    keyStatus: {},
    prefixStatus: {},
  },
  reducers: {
    receiveUserPreferences(state, action) {
      state.byKey = mergePreferenceRecords(state.byKey, normalizeUserPreferenceResponse(action.payload));
    },
    upsertUserPreferenceLocal(state, action) {
      const { key, value } = action.payload;
      if (!key) return;
      state.byKey[key] = { ...(state.byKey[key] ?? { key }), value };
      state.keyStatus[key] = { loaded: true, loading: false, error: null };
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loadUserPreferences.pending, (state, action) => {
        const scope = normalizeLoadArg(action.meta.arg);
        if (scope.key) {
          state.keyStatus[scope.key] = { ...getInitialStatus(), loading: true };
        } else if (scope.keyPrefix) {
          state.prefixStatus[scope.keyPrefix] = { ...getInitialStatus(), loading: true };
        } else {
          state.allLoading = true;
          state.allError = null;
        }
      })
      .addCase(loadUserPreferences.fulfilled, (state, action) => {
        const { scope, records } = action.payload;
        state.byKey = mergePreferenceRecords(state.byKey, records);
        if (scope.key) {
          state.keyStatus[scope.key] = { loaded: true, loading: false, error: null };
        } else if (scope.keyPrefix) {
          state.prefixStatus[scope.keyPrefix] = { loaded: true, loading: false, error: null };
        } else {
          state.allLoaded = true;
          state.allLoading = false;
          state.allError = null;
        }
      })
      .addCase(loadUserPreferences.rejected, (state, action) => {
        const scope = action.payload?.scope ?? normalizeLoadArg(action.meta.arg);
        const error = action.payload?.error ?? 'Failed to load preferences';
        if (scope.key) {
          state.keyStatus[scope.key] = { loaded: true, loading: false, error };
        } else if (scope.keyPrefix) {
          state.prefixStatus[scope.keyPrefix] = { loaded: false, loading: false, error };
        } else {
          state.allLoading = false;
          state.allError = error;
        }
      });
  },
});

export const selectUserPreferenceHydration = key => state => getPreferenceHydration(state.userPreferences, key);

export const selectUserPreferenceValue = (key, defaultValue) => state => {
  const hydration = getPreferenceHydration(state.userPreferences, key);
  return hydration.record ? hydration.record.value : defaultValue;
};

export const { receiveUserPreferences, upsertUserPreferenceLocal } = slice.actions;

const { reducer } = slice;
export default reducer;
